import { API_PATH_INTERNAL } from '@/lib/constants'
import apiService from '@/lib/services/apiService'
import { ReferralsViewPreferences } from '@/types/entities'
import {
  GetCompanyUsersParams,
  GetShareableIndividualReferralLinksParams,
  SetPublicEmailRequest,
  UpdateCompanyUserMailingListRequest,
  VerifyPublicEmailRequest,
} from '@/types/requests/company-users'
import {
  CompanyUserCollection,
  CompanyUserCollectionItem,
  CompanyUserItem,
  CompanyUserMailingListCollection,
  CompanyUserPublicEmailItem,
  ShareableIndividualReferralLinkCollection,
  VerifyPublicEmailResponse,
} from '@/types/responses/company-users'

class CompanyUserService {
  private readonly endpoint = `${API_PATH_INTERNAL}/company-users`

  async getCompanyUsers(
    params?: GetCompanyUsersParams,
  ): Promise<CompanyUserCollection> {
    if (params === undefined) {
      params = {
        'order[fullName]': 'asc',
      }
    }

    const response = await apiService.get(this.endpoint, { params })

    return response.data
  }

  async searchCompanyUsers(
    companyUserName: string,
  ): Promise<CompanyUserCollection> {
    const response = await apiService.get(this.endpoint, {
      params: {
        fullName: companyUserName,
      },
    })

    return response.data
  }

  async toggleCompanyUserNotificationsPreference(
    companyUserId: number,
    enableNotifications: boolean,
  ): Promise<CompanyUserCollectionItem> {
    const response = await apiService.patch<CompanyUserCollectionItem>(
      `${this.endpoint}/${companyUserId}`,
      {
        notify: enableNotifications,
      },
    )

    return response.data
  }

  async updateCompanyUserPermissionValue(
    companyUserId: number,
    value: IRI[],
  ): Promise<CompanyUserCollectionItem> {
    const response = await apiService.patch<CompanyUserCollectionItem>(
      `${this.endpoint}/${companyUserId}`,
      {
        selectedRoles: value,
      },
    )

    return response.data
  }

  async updateLocation(
    companyUserId: number,
    locationIri: IRI | null,
  ): Promise<void> {
    await apiService.patch(`${this.endpoint}/${companyUserId}`, {
      location: locationIri,
    })
  }

  async updateReferralsViewPreferences(
    companyUserId: number,
    preferences: ReferralsViewPreferences | null,
  ): Promise<void> {
    await apiService.patch(`${this.endpoint}/${companyUserId}`, {
      referralsViewPreferences: preferences,
    })
  }

  async delete(companyUserId: number) {
    const response = await apiService.delete(
      `${this.endpoint}/${companyUserId}`,
    )

    return response.data
  }

  async getCompanyUser(companyUserId: number): Promise<CompanyUserItem> {
    const response = await apiService.get(`${this.endpoint}/${companyUserId}`)

    return response.data
  }

  async getMailingLists(
    companyUserId: number,
  ): Promise<CompanyUserMailingListCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyUserId}/mailing-lists`,
    )

    return response.data
  }

  async updateCompanyUserMailingLists(
    companyUserId: number,
    value: UpdateCompanyUserMailingListRequest,
  ): Promise<CompanyUserCollectionItem> {
    const response = await apiService.patch<CompanyUserCollectionItem>(
      `${this.endpoint}/${companyUserId}/mailing-lists`,
      {
        mailingListSubscriptions: value,
      },
    )

    return response.data
  }

  async getShareableReferralLinks(
    companyUserId: number,
    params: GetShareableIndividualReferralLinksParams,
  ): Promise<ShareableIndividualReferralLinkCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyUserId}/shareable-referral-links`,
      { params },
    )

    return response.data
  }

  async setPublicEmail(
    companyUserId: number,
    request: SetPublicEmailRequest,
  ): Promise<CompanyUserPublicEmailItem> {
    const response = await apiService.post<CompanyUserPublicEmailItem>(
      `${this.endpoint}/${companyUserId}/public-email`,
      request,
    )

    return response.data
  }

  async resendPublicEmailVerification(
    companyUserId: number,
  ): Promise<CompanyUserPublicEmailItem> {
    const response = await apiService.post<CompanyUserPublicEmailItem>(
      `${this.endpoint}/${companyUserId}/public-email/resend`,
      {},
    )

    return response.data
  }

  async cancelPendingPublicEmail(companyUserId: number): Promise<void> {
    await apiService.delete(
      `${this.endpoint}/${companyUserId}/public-email/pending`,
    )
  }

  async removePublicEmail(companyUserId: number): Promise<void> {
    await apiService.delete(`${this.endpoint}/${companyUserId}/public-email`)
  }

  async verifyPublicEmail(
    request: VerifyPublicEmailRequest,
  ): Promise<VerifyPublicEmailResponse> {
    const response = await apiService.post<VerifyPublicEmailResponse>(
      `${this.endpoint}/public-email/verify`,
      request,
    )

    return response.data
  }

  getIri(companyUserId: number): IRI {
    return `${this.endpoint}/${companyUserId}`
  }
}

const companyUserService = new CompanyUserService()

export default companyUserService
